/* Textdialog (A9): rubrik, text och ETT fält (input eller textarea). Täcker de
   14 prompt-fallen i KanPlan — avslagsorsak, kommentar, namn på ny mall,
   "kopiera länken manuellt" (readOnly + copyButton) och "skriv RADERA" (requireText).

   Används normalt via kpPrompt() i ./dialogApi. Löser till strängen vid bekräfta
   och null vid Avbryt/Esc/klick utanför — samma kontrakt som window.prompt. */
import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Copy, X } from 'lucide-react';
import useFocusTrap from '../../documents/useFocusTrap';
import { pushEscHandler, hasEscDispatcher } from './dialogApi';
import './Dialog.css';

const DEFAULT_CONFIRM = { primary: 'OK', danger: 'Ta bort', warning: 'Fortsätt' };

export default function PromptDialog({
  open, title, message, label, placeholder, defaultValue = '', multiline = false,
  required = false, maxLength, readOnly = false, copyButton = false, requireText,
  confirmLabel, cancelLabel = 'Avbryt', variant = 'primary', onSubmit, onCancel,
}) {
  const ref = useRef(null);
  const fieldRef = useRef(null);
  const [value, setValue] = useState(defaultValue == null ? '' : String(defaultValue));
  const [typed, setTyped] = useState('');
  const [copied, setCopied] = useState(false);
  const uid = useId();
  useFocusTrap(ref, open);

  const v = ['danger', 'warning', 'primary'].includes(variant) ? variant : 'primary';
  const tomt = required && value.trim() === '';
  const olast = !!requireText && typed !== requireText;
  const locked = tomt || olast;

  useEffect(() => {
    if (!open) return undefined;
    const cancel = () => { onCancel(); };
    // Normalfallet: DialogProvider äger EN tidig window-capture-lyssnare och skickar Esc hit.
    const unregister = pushEscHandler(cancel);
    if (hasEscDispatcher()) return unregister;
    // Reserv (ingen provider, t.ex. isolerat test): egen lyssnare.
    const onKey = (e) => { if (e.key === 'Escape') { e.stopImmediatePropagation(); e.stopPropagation(); e.preventDefault(); cancel(); } };
    window.addEventListener('keydown', onKey, true);
    return () => { unregister(); window.removeEventListener('keydown', onKey, true); };
  }, [open, onCancel]);

  useEffect(() => {
    if (!open || !fieldRef.current) return;
    fieldRef.current.focus();
    if (!readOnly && typeof fieldRef.current.select === 'function' && !multiline) fieldRef.current.select();
  }, [open, readOnly, multiline]);

  if (!open) return null;

  const submit = () => { if (!locked) onSubmit(value); };

  const onFieldKey = (e) => {
    if (e.key !== 'Enter') return;
    if (multiline && !(e.ctrlKey || e.metaKey)) return;
    e.preventDefault();
    submit();
  };

  const copy = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(value);
      } else if (fieldRef.current) {
        fieldRef.current.select();
        document.execCommand('copy');
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (_) {
      if (fieldRef.current) fieldRef.current.select();
    }
  };

  const FieldTag = multiline ? 'textarea' : 'input';

  return createPortal(
    <div className="kp-dialog-overlay" role="presentation"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className={`kp-dialog kp-dialog--${v}`} role="dialog" aria-modal="true"
        aria-labelledby={`${uid}-title`} aria-describedby={message ? `${uid}-desc` : undefined} ref={ref}
        data-testid="kp-prompt-dialog">
        <div className="kp-dialog-head">
          <div className="kp-dialog-headtext">
            <div id={`${uid}-title`} className="kp-dialog-title">{title || 'Ange värde'}</div>
            {message && <div id={`${uid}-desc`} className="kp-dialog-desc">{message}</div>}
          </div>
          <button type="button" className="kp-dialog-close" onClick={onCancel} aria-label="Stäng">
            <X size={16} strokeWidth={2.5} />
          </button>
        </div>
        <div className="kp-dialog-body">
          {label && <label className="kp-dialog-label" htmlFor={`${uid}-field`}>{label}</label>}
          <div className={`kp-dialog-fieldrow${copyButton ? ' kp-dialog-fieldrow--copy' : ''}`}>
            <FieldTag
              id={`${uid}-field`}
              ref={fieldRef}
              className={`kp-dialog-input${multiline ? ' kp-dialog-input--multi' : ''}`}
              type={multiline ? undefined : 'text'}
              rows={multiline ? 4 : undefined}
              value={value}
              placeholder={placeholder}
              maxLength={maxLength}
              readOnly={readOnly}
              aria-label={label ? undefined : (title || 'Värde')}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={onFieldKey}
            />
            {copyButton && (
              <button type="button" className="kp-dialog-btn kp-dialog-btn--cancel kp-dialog-copy" onClick={copy}>
                {copied ? <Check size={14} strokeWidth={2.5} /> : <Copy size={14} strokeWidth={2.5} />}
                {copied ? 'Kopierat' : 'Kopiera'}
              </button>
            )}
          </div>
          {typeof maxLength === 'number' && !readOnly && (
            <div className="kp-dialog-hint">{value.length} / {maxLength}</div>
          )}
          {requireText && (
            <>
              <label className="kp-dialog-label kp-dialog-label--gap" htmlFor={`${uid}-require`}>
                Skriv <strong>{requireText}</strong> för att bekräfta
              </label>
              <input id={`${uid}-require`} className="kp-dialog-input" type="text" autoComplete="off"
                value={typed} onChange={(e) => setTyped(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }} />
            </>
          )}
        </div>
        <div className="kp-dialog-foot">
          <button type="button" className="kp-dialog-btn kp-dialog-btn--cancel" onClick={onCancel}>
            {readOnly && !requireText ? 'Stäng' : cancelLabel}
          </button>
          {!(readOnly && !requireText) && (
            <button type="button" className={`kp-dialog-btn kp-dialog-btn--${v}`} onClick={submit} disabled={locked}>
              {confirmLabel || DEFAULT_CONFIRM[v]}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
